import React, { useState, useEffect, useMemo, useRef } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { socket } from "../utils/socket";
import { useDashboard } from "./dashboardStore";

const CustomerSupplierData = () => {
  const pageSize = 5;

  // Selected month for filtering
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Search & pagination state
  const [search, setSearch] = useState("");
  const [pageCustomer, setPageCustomer] = useState(1);
  const [pageSupplier, setPageSupplier] = useState(1);
    
    // pagination for exporters
    const [pageExportCustomer, setPageExportCustomer] = useState(1);
    const [pageExportSupplier, setPageExportSupplier] = useState(1);


  // Error messages & modal state
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);

  // New item form state
  const [newItem, setNewItem] = useState({
    type: "supplier",
    name: "",
    typeImport: "Local",
    revenue: "",
    region: "",
    trend: "U",
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
  });

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);


    // Socket: handle new event
    useEffect(() => {
        if (!socket) return;

        const handleNewQuotation = (data) => {
        	if (data.type === "singleNewSupplier") {
                  const added = data.newSupplier;
                  const normalized = {
                    id: Number(added.Id),
                    name: added.Name,
                    purchase: Number(added.Purchase || item.purchase), // include purchase
                    region: added.Region,
                    month: data.month,
                    typeImport: added.Type || item.typeImport, // <-- fix: ensure typeImport is set correctly
                  };

                setSuppliers(prevSuppliers => [normalized, ...prevSuppliers]);
              
            }

        	if (data.type === "singleNewCustomer") {
                  const added = data.newCustomer;
                  const normalized = {
                    id: added.Id,
                    name: added.Name,
                    revenue: added.Revenue,
                    growth: added.Growth,
                    region: added.Region,
                    month: data.month,
                    typeImport: added.Type || item.typeImport, 
                  };

                setCustomers(prevCustomers => [normalized, ...prevCustomers]);
              
            }
            
            if (data.type === "CustomerDeletion") {
                setCustomers(prevCustomers => 
                    prevCustomers.filter(c => !(c.id === Number(data.id) && c.month?.slice(0, 7) === data.month))
                );
            }
            if (data.type === "SupplierDeletion") {
            
                setSuppliers(prevSuppliers => 
                    prevSuppliers.filter(c => !(c.id === Number(data.id) && c.month?.slice(0, 7) === data.month))
                );
            }
            
            // updates
            if (data.type === "singleUpdatedSupplier") {
              const updated = data.updatedSupplier;
              setSuppliers(prev =>
                prev.map(s => 
                  s.id === updated.Id
                    ? {
                        ...s,
                        name: updated.Name,
                        purchase: Number(updated.Purchase),
                        region: updated.Region,
                        month: data.month,
                        typeImport: updated.Type || "Local",
                      }
                    : s
                )
              );
            }

            if (data.type === "singleUpdatedCustomer") {
              const updated = data.updatedCustomer;
              setCustomers(prev =>
                prev.map(c =>
                  c.id === updated.Id
                    ? {
                        ...c,
                        name: updated.Name,
                        revenue: updated.Revenue,
                        growth: updated.Growth,
                        region: updated.Region,
                        month: data.month,
                        typeImport: updated.Type || "Local",
                      }
                    : c
                )
              );
            }

            
        }

        socket.on("CustomerSupplierDataAlert", handleNewQuotation);
        return () => {
            socket.off("CustomerSupplierDataAlert", handleNewQuotation);
        };
    }, [socket]);
// Subscribe to dashboard store slice
const customersDat = useDashboard(state => state.data?.customers);

// -------------------------------------
// Process customers data
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!customersDat) return;

	const processCustomers = () => {
		try {
			// Normalize each customer row
			const normalized = customersDat.map((c) => ({
				id: Number(c.Id),
				name: c.Name,
				revenue: Number(c.Revenue || 0),
				growth: c.Growth,
				region: c.Region,
				month: c.month,
				typeImport: c.Type
			}));

			// Sort by revenue descending
			normalized.sort((a, b) => b.revenue - a.revenue);

			// Update state
			setCustomers(normalized);
		} catch (err) {
			console.error("Error processing customers:", err);
		} finally {
			setLoadingCustomers(false);
		}
	};

	processCustomers();
}, [customersDat]); // Reactive: runs whenever store updates

  // Add a new customer
const handleAddCustomer = async (item) => {
  try {
    const res = await fetch(`${apiBaseUrl}/api/customers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: item.name,
        type: item.typeImport, // Import/Export
        revenue: parseFloat(item.revenue),
        growth: parseInt(item.growth) || 0,
        region: item.region,
        month: item.month || new Date().toISOString(),
      }),
    });

    if (!res.ok) throw new Error("Failed to add customer");

  
    setPageCustomer(1);
    setShowModal(false);
    resetNewItem();
  } catch (err) {
    console.error("Error adding customer:", err);
    alert("Failed to add customer. Try again.");
  }
};


  // Delete customer
const handleDeleteCustomer = async (id) => {
  try {
    // Include the selected month as a query parameter
    const res = await fetch(`${apiBaseUrl}/api/customers/${id}?month=${selectedMonth}`, {
      method: "DELETE",
    });

    if (!res.ok) throw new Error("Failed to delete customer");
    
  } catch (err) {
    console.error("Error deleting customer:", err);
    alert("Failed to delete customer. Try again.");
  }
};


  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const didInitialFetchSuppliers = useRef(false);

// Subscribe to dashboard store slice
const suppliersDat = useDashboard(state => state.data?.suppliers);

// -------------------------------------
// Process suppliers data
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!suppliersDat) return;

	const processSuppliers = () => {
		try {
			// Normalize each supplier row
			const normalized = suppliersDat.map((s) => ({
				id: Number(s.Id),
				name: s.Name,
				purchase: Number(s.Purchase || 0),
				region: s.Region,
				month: s.month,
				typeImport: s.Type,
			}));

			// Sort by purchase descending
			normalized.sort((a, b) => b.purchase - a.purchase);

			setSuppliers(normalized);
		} catch (err) {
			console.error("Error processing suppliers:", err);
		}
	};

	processSuppliers();
}, [suppliersDat]); // Reactive: runs whenever store updates


  // Compute suppliers for selected month with previous month data
  const suppliersForMonth = useMemo(() => {
    const prevMonth = (ym) => {
      const [y, m] = ym.split("-").map(Number);
      const newMonth = m === 1 ? 12 : m - 1;
      const newYear = m === 1 ? y - 1 : y;
      return `${newYear}-${String(newMonth).padStart(2, "0")}`;
    };

    return suppliers
      .filter((s) => s.month?.slice(0, 7) === selectedMonth)
      .map((s) => {
        const prev = suppliers.find(
          (p) => p.name === s.name && p.month?.slice(0, 7) === prevMonth(s.month.slice(0, 7))
        );
        return { ...s, previousMonthPurchase: prev?.purchase || 0 };
      });
  }, [suppliers, selectedMonth]);
  
  // Compute customers for selected month with previous month data
const customersForMonth = useMemo(() => {
  const prevMonth = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const newMonth = m === 1 ? 12 : m - 1;
    const newYear = m === 1 ? y - 1 : y;
    return `${newYear}-${String(newMonth).padStart(2, "0")}`;
  };
     return customers
    .filter((c) => c.month?.slice(0, 7) === selectedMonth)
    .map((c) => {
      const prev = customers.find(
        (p) => p.name === c.name && p.month?.slice(0, 7) === prevMonth(c.month.slice(0, 7))
      );
      const growth =
        prev && prev.revenue
          ? Math.round(((c.revenue - prev.revenue) / prev.revenue) * 100)
          : null;
      return { ...c, previousMonth: prev?.revenue || 0, growth };
    });
}, [customers, selectedMonth]);

  // Add a new supplier
const handleAddSupplier = async (item) => {
  try {
    const res = await fetch(`${apiBaseUrl}/api/suppliers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: item.name,
        purchase: item.purchase,
        region: item.region,
        month: item.month || new Date().toISOString(),
        type: item.typeImport, // send Import/Export
      }),
    });
    if (!res.ok) throw new Error("Failed to add supplier");


    setPageSupplier(1);
    setShowModal(false);
    resetNewItem();
  } catch (err) {
    console.error(err);
    alert("Failed to add supplier");
  }
};


// Delete supplier for the selected month
const handleDeleteSupplier = async (id) => {
  try {
    // Include the selected month as a query parameter
    const res = await fetch(`${apiBaseUrl}/api/suppliers/${id}?month=${selectedMonth}`, {
      method: "DELETE",
    });

    if (!res.ok) throw new Error("Failed to delete supplier");

  } catch (err) {
    console.error("Error deleting supplier:", err);
    alert("Failed to delete supplier. Try again.");
  }
};


  // Reset new item form
  const resetNewItem = () =>
    setNewItem({
      type: "supplier",
      name: "",
      revenue: "",
      typeImport: "Local",
      region: "",
      trend: "U",
      month: new Date().toISOString().slice(0, 7), // YYYY-MM
    });

  // Confirm deletion before executing
  const confirmDelete = async (type, id, name, deleteFn) => {
      const [year, month] = selectedMonth.split("-").map(Number);
      const monthDate = new Date(year, month - 1); // JS months are 0-based
      const monthString = monthDate.toLocaleString("en-GB", { month: "long", year: "numeric" }); // e.g., "October 2025"
    
      const proceed = window.confirm(
        `⚠️ Warning:\n\nYou are about to delete this ${type}.\n\nFrom Month: ${monthString}\n\nName: ${name}\n\nAre you sure?`
      );
    if (!proceed) return;
    await deleteFn(id);
  };

  // Validate new item form
  const validateNewItem = (item) => {
    const errs = {};
    if (!item.name) errs.name = "Name is required";
    if (item.type === "customer") {
      if (!item.revenue) errs.revenue = "Revenue is required";

      if (!item.region) errs.region = "Region is required";
    } else if (item.type === "supplier") {

      if (!item.region) errs.region = "Region is required";
    }
    return errs;
  };


  // Filter & paginate helper
  const filterAndPaginate = (data, page) => {
    const filtered = data.filter((d) =>
      Object.values(d).some((v) => String(v).toLowerCase().includes(search.toLowerCase()))
    );
    const totalPages = Math.ceil(filtered.length / pageSize) || 1;
    const currentItems = filtered.slice((page - 1) * pageSize, page * pageSize);
    return { currentItems, totalPages };
  };

    // Local tables
    const localSuppliersForMonth = suppliersForMonth.filter(s => s.typeImport  === "Local");
    const localCustomersForMonth = customersForMonth.filter(c => c.typeImport  === "Local");
    
    const { currentItems: currentLocalCustomers, totalPages: totalPagesLocalCustomer } = filterAndPaginate(
      localCustomersForMonth,
      pageCustomer
    );
    const { currentItems: currentLocalSuppliers, totalPages: totalPagesLocalSupplier } = filterAndPaginate(
      localSuppliersForMonth,
      pageSupplier
    );
    
    // Export tables
    const exportSuppliersForMonth = suppliersForMonth.filter(s => s.typeImport  === "Export");
    const exportCustomersForMonth = customersForMonth.filter(c => c.typeImport  === "Export");
    
    const { currentItems: currentExportCustomers, totalPages: totalPagesExportCustomer } = filterAndPaginate(
      exportCustomersForMonth,
      pageExportCustomer
    );
    const { currentItems: currentExportSuppliers, totalPages: totalPagesExportSupplier } = filterAndPaginate(
      exportSuppliersForMonth,
      pageExportSupplier
    );

  
  // selectedMonth is in "YYYY-MM" format
const handleNextMonth = () => {
  const [year, month] = selectedMonth.split("-").map(Number);
  const newMonth = month === 12 ? 1 : month + 1;
  const newYear = month === 12 ? year + 1 : year;
  setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
};

const handlePrevMonth = () => {
  const [year, month] = selectedMonth.split("-").map(Number);
  const newMonth = month === 1 ? 12 : month - 1;
  const newYear = month === 1 ? year - 1 : year;
  setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
};


  // Reusable table component
  const Table = ({ title, currentItems, page, totalPages, onDelete, columns, onPageChange }) => (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <h2 className="text-lg font-bold px-4 py-3 border-b">{title}</h2>
      <table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-4 text-left font-medium text-gray-700 ${col.width || ""}`}
              >
                {col.label}
              </th>
            ))}
            <th className="px-3 py-4 text-center font-medium text-gray-700 w-20">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {currentItems.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="px-3 py-4 text-center text-gray-500 italic">
                No items found
              </td>
            </tr>
          )}
          {currentItems.map((item) => (
            <tr key={item.id} className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 text-gray-600 ${
                    col.align === "right"
                      ? "text-right"
                      : col.align === "center"
                      ? "text-center"
                      : "text-left"
                  }`}
                >
                  {col.format ? col.format(item) : item[col.key]}
                </td>
              ))}
              <td className="px-3 py-2 flex flex-row gap-2 text-center">

               <button
                 className="px-2 py-1 text-sm text-white bg-orange-600 rounded-md hover:bg-orange-700 cursor-pointer"
                 onClick={() => modifyItem(item)}
               >
                 Modify
               </button>
                <button
                  className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
                  onClick={() => onDelete(item.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <button
            className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
      
      
    </div>
  );
    const [isEditing, setIsEditing] = useState(false);
    
    const modifyItem = (item) => {
        // Pre-fill the modal with the selected item's data
        setNewItem({
            id: item.id,
            type: item.revenue !== undefined ? "customer" : "supplier", // infer type
            name: item.name ?? "",
            typeImport: item.typeImport ?? "Local",
            revenue: item.revenue ?? "",
            purchase: item.purchase ?? "",
            region: item.region ?? "",
            trend: item.trend ?? "U",
            month: item.month?.slice(0, 7) ?? new Date().toISOString().slice(0, 7),
        });
        setIsEditing(true); 
    
        // Show modal
        setShowModal(true);
        
    };
    
    const handleUpdateCustomer = async (item) => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/customers/${item.id}`, {
          method: "PUT", // or PATCH depending on your backend
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: item.name,
            type: item.typeImport,
            revenue: parseFloat(item.revenue),
            region: item.region,
            month: item.month,
          }),
        });
        if (!res.ok) throw new Error("Failed to update customer");
        // optionally refresh state
        setShowModal(false);
        setIsEditing(false);
      } catch (err) {
        console.error(err);
        alert("Failed to update customer");
      }
    };
    
    const handleUpdateSupplier = async (item) => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/suppliers/${item.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: item.name,
            type: item.typeImport,
            purchase: item.purchase,
            region: item.region,
            month: item.month,
          }),
        });
        if (!res.ok) throw new Error("Failed to update supplier");
        setShowModal(false);
        setIsEditing(false);
      } catch (err) {
        console.error(err);
        alert("Failed to update supplier");
      }
    };


  return (
    <div className="w-full max-w-8xl mx-auto flex-1 overflow-y-auto p-4">
      {/* Month Selector */}
    <div className="mb-4">
      <div className="flex items-center justify-center">
        <button
          onClick={handlePrevMonth}
          className="px-4 py-2 bg-blue-500 cursor-pointer rounded hover:bg-blue-600 mr-3"
        >
          Prev Month
        </button>
    
        <button
          onClick={() => setSelectedMonth(new Date().toISOString().slice(0, 7))}
          className="px-4 py-2 bg-green-500 cursor-pointer rounded hover:bg-green-600 mr-3 text-white"
        >
          Now
        </button>
    
        <div className="flex flex-col">
          <label className="mr-2 font-bold text-blue-500">View Month:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border rounded px-3 py-1"
          />
          <span className="text-sm italic text-gray-500 mt-1">
            You can also click the date
          </span>
        </div>
    
        <button
          onClick={handleNextMonth}
          className="px-4 py-2 bg-blue-500 cursor-pointer rounded hover:bg-blue-600 ml-3"
        >
          Next Month
        </button>
      </div>
    </div>



      {/* Search + Add Button */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
        <input
          type="text"
          placeholder="Search across all tables..."
          className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPageCustomer(1);
            setPageSupplier(1);
          }}
        />
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
          onClick={() => setShowModal(true)}
        >
          Add Item
        </button>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <Table
          title="Local Suppliers"
          currentItems={currentLocalSuppliers}
          page={pageSupplier}
          totalPages={totalPagesLocalSupplier}
            onDelete={(id) => {
              const item = currentLocalSuppliers.find(s => s.id === id);
              confirmDelete("supplier", id, item.name, handleDeleteSupplier);
            }}

          onPageChange={setPageSupplier}
          columns={[
            { key: "name", label: "Supplier" },
              {
                key: "purchase",
                label: "Cost",
                format: (row) => `${Math.round(row.purchase).toLocaleString()} AED`
              },
              {
                key: "previousMonthPurchase",
                label: "Previous Month",
                format: (row) => {
                
                    if (row.previousMonthPurchase === 0) return <span className="text-orange-500"> No Data </span>

                
                    return `${Math.round(row.previousMonthPurchase).toLocaleString()} AED`
                }
              },
            { key: "region", label: "Region" },
            {
              key: "growth",
              label: "Growth",
              format: (row) => {
                if (!row.previousMonthPurchase) return "—"; // no previous data
                const growthPercent = ((row.purchase - row.previousMonthPurchase) / row.previousMonthPurchase) * 100;
                const color = growthPercent > 0 ? "text-green-600" : growthPercent < 0 ? "text-red-600" : "text-gray-700";
                const sign = growthPercent > 0 ? "+" : "";

                return <span className={color}>{sign}{growthPercent.toFixed(1)}%</span>;
              },
            }

          ]}
        />
        <Table
          title="Local Customers"
        currentItems={currentLocalCustomers}
          page={pageCustomer}
          totalPages={totalPagesLocalCustomer}
            onDelete={(id) => {
              const item = currentLocalCustomers.find(c => c.id === id);
              confirmDelete("customer", id, item.name, handleDeleteCustomer);
            }}
          onPageChange={setPageCustomer}
            columns={[
              { key: "name", label: "Customer" },
              {
                key: "revenue",
                label: "Revenue",
                format: (row) => 
                  row.revenue !== null && row.revenue !== undefined
                    ? `${Math.round(row.revenue).toLocaleString()} AED`
                    : "—",
              },
              { key: "region", label: "Region" },
                {
                  key: "previousMonth",
                  label: "Previous Month",
                  format: (row) =>
                    row.previousMonth !== null && row.previousMonth !== undefined && row.previousMonth !== 0
                      ? `${Math.round(row.previousMonth).toLocaleString()} AED`
                      : <span className="text-orange-500"> No Data </span>,
                },

              {
                  key: "growth",
                  label: "Growth",
                  format: (row) => {
                    if (row.growth === null || row.growth === undefined) return "—";
                    const color = row.growth > 0 ? "text-green-600" : row.growth < 0 ? "text-red-600" : "text-gray-700";
                    const sign = row.growth > 0 ? "+" : "";
                    return <span className={color}>{sign}{row.growth}%</span>;
              },
              }
            ]}

        />
      </div>

      {/* Export Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Table
          title="Export Suppliers"
          currentItems={currentExportSuppliers}
          page={pageExportSupplier}
          totalPages={totalPagesExportSupplier}
            onDelete={(id) => {
              const item = currentExportSuppliers.find(s => s.id === id);
              confirmDelete("export supplier", id, item.name, handleDeleteSupplier);
            }}
          onPageChange={setPageExportSupplier}
          columns={[
            { key: "name", label: "Supplier" },
  
            {
              key: "purchase",
              label: "Cost",
              format: (row) => `${Math.round(row.purchase).toLocaleString()} AED`
            },
            {
              key: "previousMonthPurchase",
              label: "Previous Month",
              format: (row) =>
                row.previousMonthPurchase
                  ? `${Math.round(row.previousMonthPurchase).toLocaleString()} AED`
                  : <span className="text-orange-500"> No Data </span>,
            },

            

            { key: "region", label: "Region" },

              {
                key: "growth",
                label: "Growth",
                format: (row) => {
                  if (!row.previousMonthPurchase) return "—"; // no previous data
                  const growthPercent = ((row.purchase - row.previousMonthPurchase) / row.previousMonthPurchase) * 100;
                  const color = growthPercent > 0 ? "text-green-600" : growthPercent < 0 ? "text-red-600" : "text-gray-700";
                  const sign = growthPercent > 0 ? "+" : "";

                  return <span className={color}>{sign}{growthPercent.toFixed(1)}%</span>;
                },
            }
          ]}
        />
        <Table
          title="Export Customers"
          currentItems={currentExportCustomers}
          page={pageExportCustomer}
          totalPages={totalPagesExportCustomer}
            onDelete={(id) => {
              const item = currentExportCustomers.find(c => c.id === id);
              confirmDelete("export customer", id, item.name, handleDeleteCustomer);
        }}

          onPageChange={setPageExportCustomer}
            columns={[
              { key: "name", label: "Customer" },
              {
                key: "revenue",
                label: "Revenue",
                format: (row) => 
                  row.revenue !== null && row.revenue !== undefined
                    ? `${Math.round(row.revenue).toLocaleString()} AED`
                    : "—",
              },
              { key: "region", label: "Region" },
                {
                  key: "previousMonth",
                  label: "Previous Month",
                  format: (row) =>
                    row.previousMonth
                      ? `${Math.round(row.previousMonth).toLocaleString()} AED`
                      : <span className="text-orange-500"> No Data </span>,
                },

              {
              key: "growth",
              label: "Growth",
              format: (row) => {
                if (row.growth === null || row.growth === undefined) return "—";
                const color = row.growth > 0 ? "text-green-600" : row.growth < 0 ? "text-red-600" : "text-gray-700";
                const sign = row.growth > 0 ? "+" : "";
                return <span className={color}>{sign}{row.growth}%</span>;
              },
              }
            ]}



        />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
            <h2 className="text-lg font-semibold mb-4">Add New Item</h2>

            <div className="flex flex-col gap-3">
              {/* Type Selector */}
            <label className="block text-sm font-medium mb-1">Customer/Supplier</label>

              <select
              className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 
                ${isEditing ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-white text-gray-900"}`}
                value={newItem.type}
                onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                  disabled={isEditing} 

              >
                <option value="supplier">Supplier</option>
                <option value="customer">Customer</option>
              </select>

              {/* Name */}
                <label className="block text-sm font-medium mb-1">Name</label>

              <input
                type="text"
                placeholder="Name"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              />
              {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
             
             {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                value={newItem.typeImport}
                onChange={(e) => setNewItem({ ...newItem, typeImport: e.target.value })}
              >
                <option value="Local">Import</option>
                <option value="Export">Export</option>
              </select>
              {errors.typeImport && <p className="text-red-600 text-sm mt-1">{errors.typeImport}</p>}
            </div>
            
                <label className="block text-sm font-medium mb-1">Revenue/Cost</label>

              {/* Revenue - only for customer */}
              {newItem.type === "customer" && (
                <>
                  <input
                    type="number"
                    placeholder="Revenue"
                    className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                    value={newItem.revenue}
                    onChange={(e) => setNewItem({ ...newItem, revenue: e.target.value })}
                  />
                  {errors.revenue && <p className="text-red-600 text-sm mt-1">{errors.revenue}</p>}
                </>
              )}

              {/* Cost only for supplier */}

              {newItem.type === "supplier" && (
                <>
                  <input
                    type="number"
                    placeholder="Cost"
                    className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                    value={newItem.purchase}
                    onChange={(e) => setNewItem({ ...newItem, purchase: e.target.value })}
                  />
                  {errors.purchase && <p className="text-red-600 text-sm mt-1">{errors.purchase}</p>}
                </>
              )}

                <label className="block text-sm font-medium mb-1">Region</label>

              {/* Region */}
              <input
                type="text"
                placeholder="Region"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                value={newItem.region}
                onChange={(e) => setNewItem({ ...newItem, region: e.target.value })}
              />
              {errors.region && <p className="text-red-600 text-sm mt-1">{errors.region}</p>}

              {/* Month */}
              <div>
                <label className="block text-sm font-medium mb-1">Month</label>
                <input
                  type="month"
                  value={newItem.month}
                  onChange={(e) => setNewItem({ ...newItem, month: e.target.value })}
  className={`w-full border rounded px-3 py-1 
    ${isEditing ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-white text-gray-900"}`}
                  disabled={isEditing}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
          onClick={() => {
            const errs = validateNewItem(newItem);
            if (Object.keys(errs).length > 0) {
              setErrors(errs);
              return;
            }
            setErrors({});
        
            if (isEditing) {
              // Update existing item
              if (newItem.type === "customer") handleUpdateCustomer(newItem);
              else handleUpdateSupplier(newItem);
            } else {
              // Add new item
              if (newItem.type === "customer") handleAddCustomer(newItem);
              else handleAddSupplier(newItem);
            }
          }}
        >
          {isEditing ? "Update Item" : "Add Item"}
        </button>

            </div>

            {/* Close Icon */}
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={() => setShowModal(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSupplierData;
